import { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { 
  ArrowLeft, Save, Eye, Send, Plus, Trash2, GripVertical, Settings,
  Type, AlignLeft, Hash, Mail, Calendar, Clock, List, CheckSquare,
  Circle, Star, Upload, ToggleLeft, Users, Shield, EyeOff, FileText
} from 'lucide-react';
import AppLayout from '../components/AppLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Checkbox } from '../components/ui/checkbox';
import api from '../lib/api';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import RichTextEditor from '../components/ui/rich-text-editor';

// Base field types available to all users
const BASE_FIELD_TYPES = [
  { id: 'text', label: 'Short Text', icon: Type, description: 'Single line text input' },
  { id: 'textarea', label: 'Long Text', icon: AlignLeft, description: 'Multi-line text area' },
  { id: 'richtext', label: 'Rich Text', icon: FileText, description: 'Formatted text with styling options' },
  { id: 'number', label: 'Number', icon: Hash, description: 'Numeric input' },
  { id: 'email', label: 'Email', icon: Mail, description: 'Email address input' },
  { id: 'date', label: 'Date', icon: Calendar, description: 'Date picker' },
  { id: 'time', label: 'Time', icon: Clock, description: 'Time picker' },
  { id: 'select', label: 'Dropdown', icon: List, description: 'Single selection dropdown' },
  { id: 'multiselect', label: 'Multi-Select', icon: CheckSquare, description: 'Multiple selections' },
  { id: 'radio', label: 'Radio Buttons', icon: Circle, description: 'Single choice options' },
  { id: 'checkbox', label: 'Checkboxes', icon: CheckSquare, description: 'Multiple choice options' },
  { id: 'rating', label: 'Rating', icon: Star, description: 'Star rating scale' },
];

// Admin-only field types
const ADMIN_FIELD_TYPES = [
  { id: 'file', label: 'File Upload', icon: Upload, description: 'File attachment (Admin only)' },
  { id: 'user_selector', label: 'User Selector', icon: Users, description: 'Select users from organization (Admin only)' },
];

const FormBuilder = () => {
  const navigate = useNavigate();
  const { formId } = useParams();
  const [searchParams] = useSearchParams();
  const workspaceId = searchParams.get('workspace');
  const isGlobal = searchParams.get('global') === 'true';
  const { user } = useAuth();
  
  // Check if user is admin
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';
  
  // Field types available based on user role
  const FIELD_TYPES = isAdmin 
    ? [...BASE_FIELD_TYPES, ...ADMIN_FIELD_TYPES] 
    : BASE_FIELD_TYPES;
  
  const [formName, setFormName] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [fields, setFields] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(!!formId);
  const [formStatus, setFormStatus] = useState('draft');
  const [formIsGlobal, setFormIsGlobal] = useState(isGlobal);
  
  // Form type and feedback settings
  const [formType, setFormType] = useState('standard');
  const [approvalStrategy, setApprovalStrategy] = useState('submitter_approver');
  const [allowAnonymous, setAllowAnonymous] = useState(false);
  const [anonymousVisibleTo, setAnonymousVisibleTo] = useState([]);
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
  
  // Field editor state
  const [editingField, setEditingField] = useState(null);
  const [fieldEditorOpen, setFieldEditorOpen] = useState(false);
  
  // User groups for User Selector field
  const [userGroups, setUserGroups] = useState([]);
  
  // Preview mode
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewResponses, setPreviewResponses] = useState({});
  const [previewUsersForFields, setPreviewUsersForFields] = useState({});
  
  // Drag state
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [isOwner, setIsOwner] = useState(true);

  useEffect(() => {
    if (formId) {
      fetchForm();
    }
    // Fetch user groups for admin
    if (isAdmin) {
      fetchUserGroups();
    }
  }, [formId, isAdmin]);

  const fetchUserGroups = async () => {
    try {
      const response = await api.get('/admin/groups');
      setUserGroups(response.data.groups || []);
    } catch (error) {
      console.error('Failed to fetch user groups:', error);
    }
  };

  const fetchForm = async () => {
    try {
      const response = await api.get(`/forms/${formId}`);
      const form = response.data;
      setFormName(form.name);
      setFormDescription(form.description || '');
      setFields(form.fields || []);
      setFormStatus(form.status);
      setFormIsGlobal(form.is_global || false);
      setIsOwner(form.is_owner || false);
      // Load form type and feedback settings
      setFormType(form.form_type || 'standard');
      setApprovalStrategy(form.approval_strategy || 'submitter_approver');
      setAllowAnonymous(form.allow_anonymous || false);
      setAnonymousVisibleTo(form.anonymous_visible_to || []);
      
      // If not owner, redirect back to forms page
      if (!form.is_owner) {
        toast.error('Only form owner can edit this form');
        navigate('/forms');
        return;
      }
    } catch (error) {
      toast.error('Failed to load form');
      navigate('/forms');
    } finally {
      setLoading(false);
    }
  };

  const generateFieldId = () => `field_${Math.random().toString(36).substr(2, 8)}`;

  const addField = (fieldType) => {
    const typeConfig = FIELD_TYPES.find(t => t.id === fieldType);
    const newField = {
      field_id: generateFieldId(),
      field_type: fieldType,
      label: `New ${typeConfig?.label || 'Field'}`,
      placeholder: '',
      required: false,
      options: ['select', 'multiselect', 'radio', 'checkbox'].includes(fieldType) 
        ? [{ option_id: `opt_${Date.now()}`, label: 'Option 1', value: 'option_1' }]
        : [],
      min_value: fieldType === 'rating' ? 1 : null,
      max_value: fieldType === 'rating' ? 5 : null,
      help_text: '',
      order: fields.length,
      // User selector default config
      user_selector_config: fieldType === 'user_selector' ? {
        multi_select: false,
        exclude_self: true,
        allow_select_all: false,
        allowed_groups: [] // Groups whose members will appear in the selector
      } : null
    };
    
    setFields([...fields, newField]);
    setEditingField(newField);
    setFieldEditorOpen(true);
  };

  const updateField = (fieldId, updates) => {
    setFields(fields.map(f => 
      f.field_id === fieldId ? { ...f, ...updates } : f
    ));
  };

  const deleteField = (fieldId) => {
    setFields(fields.filter(f => f.field_id !== fieldId));
    if (editingField?.field_id === fieldId) {
      setFieldEditorOpen(false);
      setEditingField(null);
    }
  };

  const openFieldEditor = (field) => {
    setEditingField({ ...field });
    setFieldEditorOpen(true);
  };

  const saveFieldEdits = () => {
    if (editingField) {
      updateField(editingField.field_id, editingField);
    }
    setFieldEditorOpen(false);
    setEditingField(null);
  };

  const addOption = () => {
    if (!editingField) return;
    const newOption = {
      option_id: `opt_${Date.now()}`,
      label: `Option ${(editingField.options?.length || 0) + 1}`,
      value: `option_${(editingField.options?.length || 0) + 1}`
    };
    setEditingField({
      ...editingField,
      options: [...(editingField.options || []), newOption]
    });
  };

  const updateOption = (optionId, updates) => {
    if (!editingField) return;
    setEditingField({
      ...editingField,
      options: editingField.options.map(opt =>
        opt.option_id === optionId ? { ...opt, ...updates } : opt
      )
    });
  };

  const deleteOption = (optionId) => {
    if (!editingField) return;
    setEditingField({
      ...editingField,
      options: editingField.options.filter(opt => opt.option_id !== optionId)
    });
  };

  // Drag and drop handlers
  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;
    
    const newFields = [...fields];
    const draggedField = newFields[draggedIndex];
    newFields.splice(draggedIndex, 1);
    newFields.splice(index, 0, draggedField);
    
    // Update order
    newFields.forEach((f, i) => f.order = i);
    
    setFields(newFields);
    setDraggedIndex(index);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  const handleSave = async () => {
    if (!formName.trim()) {
      toast.error('Form name is required');
      return;
    }
    
    setSaving(true);
    try {
      const formData = {
        name: formName.trim(),
        description: formDescription.trim(),
        fields: fields.map((f, idx) => ({ ...f, order: idx })),
        workspace_id: formIsGlobal ? null : workspaceId,
        is_global: formIsGlobal,
        // Form type and feedback settings
        form_type: formType,
        approval_strategy: approvalStrategy,
        allow_anonymous: allowAnonymous,
        anonymous_visible_to: anonymousVisibleTo
      };
      
      if (formId) {
        await api.patch(`/forms/${formId}`, formData);
        toast.success('Form saved!');
      } else {
        const response = await api.post('/forms', formData);
        toast.success('Form created!');
        navigate(`/forms/builder/${response.data.form_id}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save form');
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    if (!formId) {
      toast.error('Save the form first');
      return;
    }
    
    if (fields.length === 0) {
      toast.error('Add at least one field before publishing');
      return;
    }
    
    try {
      await api.post(`/forms/${formId}/publish`);
      setFormStatus('active');
      toast.success('Form published!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to publish form');
    }
  };

  const getFieldIcon = (fieldType) => {
    const config = FIELD_TYPES.find(t => t.id === fieldType);
    return config?.icon || Type;
  };

  // Preview form functionality
  const openPreview = async () => {
    // Initialize preview responses
    const initialResponses = {};
    fields.forEach(field => {
      if (field.field_type === 'checkbox' || field.field_type === 'multiselect') {
        initialResponses[field.field_id] = [];
      } else if (field.field_type === 'user_selector' && field.user_selector_config?.multi_select) {
        initialResponses[field.field_id] = [];
      } else {
        initialResponses[field.field_id] = '';
      }
    });
    setPreviewResponses(initialResponses);
    
    // Fetch users for user_selector fields with group restrictions
    const usersForFields = {};
    for (const field of fields) {
      if (field.field_type === 'user_selector') {
        const allowedGroups = field.user_selector_config?.allowed_groups || [];
        try {
          let url = '/users/all?exclude_self=true';
          if (allowedGroups.length > 0) {
            url += `&group_ids=${allowedGroups.join(',')}`;
          }
          const response = await api.get(url);
          usersForFields[field.field_id] = response.data.users || [];
        } catch (error) {
          console.error('Failed to fetch users for preview:', error);
          usersForFields[field.field_id] = [];
        }
      }
    }
    setPreviewUsersForFields(usersForFields);
    setPreviewOpen(true);
  };

  const updatePreviewResponse = (fieldId, value) => {
    setPreviewResponses(prev => ({ ...prev, [fieldId]: value }));
  };

  const renderPreviewField = (field) => {
    const value = previewResponses[field.field_id];
    const Icon = getFieldIcon(field.field_type);
    
    const fieldLabel = (
      <Label className="flex items-center gap-2 mb-2">
        <Icon className="w-4 h-4 text-muted-foreground" />
        {field.label}
        {field.required && <span className="text-red-500">*</span>}
      </Label>
    );

    switch (field.field_type) {
      case 'text':
      case 'email':
        return (
          <div key={field.field_id} className="space-y-2">
            {fieldLabel}
            <Input
              type={field.field_type}
              value={value || ''}
              onChange={(e) => updatePreviewResponse(field.field_id, e.target.value)}
              placeholder={field.placeholder}
            />
          </div>
        );
      case 'textarea':
        return (
          <div key={field.field_id} className="space-y-2">
            {fieldLabel}
            <Textarea
              value={value || ''}
              onChange={(e) => updatePreviewResponse(field.field_id, e.target.value)}
              placeholder={field.placeholder}
              rows={3}
            />
          </div>
        );
      case 'number':
        return (
          <div key={field.field_id} className="space-y-2">
            {fieldLabel}
            <Input
              type="number"
              value={value || ''}
              onChange={(e) => updatePreviewResponse(field.field_id, e.target.value)}
              placeholder={field.placeholder}
              min={field.min_value}
              max={field.max_value}
            />
          </div>
        );
      case 'date':
        return (
          <div key={field.field_id} className="space-y-2">
            {fieldLabel}
            <Input
              type="date"
              value={value || ''}
              onChange={(e) => updatePreviewResponse(field.field_id, e.target.value)}
            />
          </div>
        );
      case 'time':
        return (
          <div key={field.field_id} className="space-y-2">
            {fieldLabel}
            <Input
              type="time"
              value={value || ''}
              onChange={(e) => updatePreviewResponse(field.field_id, e.target.value)}
            />
          </div>
        );
      case 'select':
        return (
          <div key={field.field_id} className="space-y-2">
            {fieldLabel}
            <Select value={value || '_placeholder_'} onValueChange={(v) => v !== '_placeholder_' && updatePreviewResponse(field.field_id, v)}>
              <SelectTrigger>
                <SelectValue placeholder={field.placeholder || 'Select an option'} />
              </SelectTrigger>
              <SelectContent>
                {(field.options || []).map((opt, i) => {
                  const optLabel = typeof opt === 'object' ? (opt.label || opt.value || '') : opt;
                  const optValue = typeof opt === 'object' ? (opt.value || opt.label || String(i)) : opt;
                  return <SelectItem key={i} value={optValue}>{optLabel}</SelectItem>;
                })}
              </SelectContent>
            </Select>
          </div>
        );
      case 'radio':
        return (
          <div key={field.field_id} className="space-y-2">
            {fieldLabel}
            <div className="space-y-2">
              {(field.options || []).map((opt, i) => {
                const optLabel = typeof opt === 'object' ? (opt.label || opt.value || '') : opt;
                const optValue = typeof opt === 'object' ? (opt.value || opt.label || String(i)) : opt;
                return (
                  <div key={i} className="flex items-center gap-2">
                    <input
                      type="radio"
                      id={`${field.field_id}-${i}`}
                      name={field.field_id}
                      value={optValue}
                      checked={value === optValue}
                      onChange={() => updatePreviewResponse(field.field_id, optValue)}
                      className="h-4 w-4"
                    />
                    <label htmlFor={`${field.field_id}-${i}`} className="text-sm">{optLabel}</label>
                  </div>
                );
              })}
            </div>
          </div>
        );
      case 'checkbox':
      case 'multiselect':
        const selectedValues = value || [];
        return (
          <div key={field.field_id} className="space-y-2">
            {fieldLabel}
            <div className="space-y-2">
              {(field.options || []).map((opt, i) => {
                const optLabel = typeof opt === 'object' ? (opt.label || opt.value || '') : opt;
                const optValue = typeof opt === 'object' ? (opt.value || opt.label || String(i)) : opt;
                return (
                  <div key={i} className="flex items-center gap-2">
                    <Checkbox
                      id={`${field.field_id}-${i}`}
                      checked={selectedValues.includes(optValue)}
                      onCheckedChange={(checked) => {
                        const newValues = checked 
                          ? [...selectedValues, optValue]
                          : selectedValues.filter(v => v !== optValue);
                        updatePreviewResponse(field.field_id, newValues);
                      }}
                    />
                    <label htmlFor={`${field.field_id}-${i}`} className="text-sm">{optLabel}</label>
                  </div>
                );
              })}
            </div>
          </div>
        );
      case 'rating':
        const maxRating = field.max_value || 5;
        return (
          <div key={field.field_id} className="space-y-2">
            {fieldLabel}
            <div className="flex gap-1">
              {[...Array(maxRating)].map((_, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => updatePreviewResponse(field.field_id, i + 1)}
                  className={`text-2xl ${(value || 0) > i ? 'text-yellow-400' : 'text-gray-300'}`}
                >
                  ★
                </button>
              ))}
            </div>
          </div>
        );
      case 'user_selector':
        const users = previewUsersForFields[field.field_id] || [];
        const config = field.user_selector_config || {};
        const isMulti = config.multi_select;
        const selectedUsers = isMulti ? (value || []) : (value ? [value] : []);
        const allowedGroups = config.allowed_groups || [];
        
        return (
          <div key={field.field_id} className="space-y-2">
            {fieldLabel}
            {allowedGroups.length > 0 && (
              <p className="text-xs text-muted-foreground mb-2">
                Showing users from {allowedGroups.length} selected group(s)
              </p>
            )}
            <Select 
              value={isMulti ? '_multi_' : (value || '_placeholder_')} 
              onValueChange={(v) => !isMulti && v !== '_placeholder_' && updatePreviewResponse(field.field_id, v)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select user(s)" />
              </SelectTrigger>
              <SelectContent>
                {users.length === 0 ? (
                  <SelectItem value="_no_users_" disabled>No users available</SelectItem>
                ) : (
                  users.map((u) => (
                    <SelectItem key={u.user_id} value={u.user_id}>
                      {u.name} ({u.email})
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
            {users.length > 0 && (
              <p className="text-xs text-green-600">{users.length} user(s) available</p>
            )}
          </div>
        );
      case 'file':
        return (
          <div key={field.field_id} className="space-y-2">
            {fieldLabel}
            <Input type="file" disabled className="cursor-not-allowed" />
            <p className="text-xs text-muted-foreground">File upload disabled in preview</p>
          </div>
        );
      case 'richtext':
        return (
          <div key={field.field_id} className="space-y-2">
            {fieldLabel}
            <RichTextEditor
              value={previewResponses[field.field_id] || ''}
              onChange={(html) => setPreviewResponses(prev => ({ ...prev, [field.field_id]: html }))}
              placeholder={field.placeholder || 'Start typing...'}
              minHeight="120px"
            />
          </div>
        );
      default:
        return (
          <div key={field.field_id} className="space-y-2">
            {fieldLabel}
            <Input placeholder={field.placeholder} disabled />
          </div>
        );
    }
  };

  if (loading) {
    return (
      <AppLayout>
      <div className="p-6 flex items-center justify-center">
        <p className="text-muted-foreground">Loading form...</p>
      </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-card border-b border-border">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/forms')}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <Input
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                placeholder="Untitled Form"
                className="text-lg font-semibold border-0 p-0 h-auto focus-visible:ring-0 bg-transparent"
                data-testid="form-name-input"
              />
              <div className="flex items-center gap-2 mt-1">
                {formStatus !== 'draft' && (
                  <Badge variant={formStatus === 'active' ? 'success' : 'secondary'}>
                    {formStatus}
                  </Badge>
                )}
                {formType === 'feedback' && (
                  <Badge variant="outline" className="border-purple-500 text-purple-600">
                    <Users className="w-3 h-3 mr-1" />
                    Feedback Form
                  </Badge>
                )}
                {allowAnonymous && (
                  <Badge variant="outline" className="border-gray-500 text-gray-600">
                    <EyeOff className="w-3 h-3 mr-1" />
                    Anonymous
                  </Badge>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => setSettingsDialogOpen(true)} data-testid="form-settings-btn">
              <Settings className="w-4 h-4 mr-2" />
              Settings
            </Button>
            <Button 
              variant="outline" 
              onClick={openPreview} 
              disabled={fields.length === 0}
              data-testid="form-preview-btn"
            >
              <Eye className="w-4 h-4 mr-2" />
              Preview
            </Button>
            <Button variant="outline" onClick={handleSave} disabled={saving}>
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Saving...' : 'Save'}
            </Button>
            {formStatus === 'draft' && (
              <Button 
                onClick={handlePublish} 
                className="bg-[#4A9CC7] hover:bg-[#3d8ab5]"
                disabled={!formId || fields.length === 0}
              >
                <Send className="w-4 h-4 mr-2" />
                Publish
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Form Fields (Main Area) */}
          <div className="lg:col-span-2 space-y-4">
            {/* Form Description */}
            <Card>
              <CardContent className="pt-6">
                <Label>Description (optional)</Label>
                <Textarea
                  value={formDescription}
                  onChange={(e) => setFormDescription(e.target.value)}
                  placeholder="Add a description for your form..."
                  className="mt-2"
                  rows={2}
                />
              </CardContent>
            </Card>

            {/* Fields List */}
            {fields.length === 0 ? (
              <Card className="border-dashed">
                <CardContent className="py-12 text-center">
                  <p className="text-muted-foreground mb-4">
                    No fields yet. Add fields from the panel on the right.
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {fields.map((field, index) => {
                  const FieldIcon = getFieldIcon(field.field_type);
                  return (
                    <Card
                      key={field.field_id}
                      className={`cursor-pointer hover:shadow-md transition-all ${
                        draggedIndex === index ? 'opacity-50' : ''
                      }`}
                      draggable
                      onDragStart={(e) => handleDragStart(e, index)}
                      onDragOver={(e) => handleDragOver(e, index)}
                      onDragEnd={handleDragEnd}
                      onClick={() => openFieldEditor(field)}
                      data-testid={`field-${field.field_id}`}
                    >
                      <CardContent className="py-4">
                        <div className="flex items-start gap-3">
                          <div className="cursor-grab mt-1">
                            <GripVertical className="w-5 h-5 text-muted-foreground" />
                          </div>
                          <div 
                            className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                            style={{ backgroundColor: '#4A9CC720', color: '#4A9CC7' }}
                          >
                            <FieldIcon className="w-4 h-4" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="font-medium truncate">{field.label}</span>
                              {field.required && (
                                <Badge variant="destructive" className="text-xs">Required</Badge>
                              )}
                            </div>
                            <p className="text-sm text-muted-foreground">
                              {FIELD_TYPES.find(t => t.id === field.field_type)?.label}
                            </p>
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-muted-foreground hover:text-red-500"
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteField(field.field_id);
                            }}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>

          {/* Field Types Panel */}
          <div className="space-y-4">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium">Add Field</CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="grid grid-cols-2 gap-2">
                  {FIELD_TYPES.map(type => {
                    const Icon = type.icon;
                    return (
                      <button
                        key={type.id}
                        onClick={() => addField(type.id)}
                        className="flex flex-col items-center gap-1 p-3 rounded-lg border border-border hover:border-[#4A9CC7] hover:bg-[#4A9CC7]/5 transition-colors text-center"
                        data-testid={`add-field-${type.id}`}
                      >
                        <Icon className="w-5 h-5 text-[#4A9CC7]" />
                        <span className="text-xs font-medium">{type.label}</span>
                      </button>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Field Editor Dialog */}
      <Dialog open={fieldEditorOpen} onOpenChange={setFieldEditorOpen}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5 text-[#4A9CC7]" />
              Edit Field
            </DialogTitle>
          </DialogHeader>
          
          {editingField && (
            <div className="space-y-4">
              <div>
                <Label>Label</Label>
                <Input
                  value={editingField.label}
                  onChange={(e) => setEditingField({ ...editingField, label: e.target.value })}
                  placeholder="Field label"
                  className="mt-1"
                  data-testid="field-label-input"
                />
              </div>

              <div>
                <Label>Placeholder</Label>
                <Input
                  value={editingField.placeholder || ''}
                  onChange={(e) => setEditingField({ ...editingField, placeholder: e.target.value })}
                  placeholder="Placeholder text"
                  className="mt-1"
                />
              </div>

              <div>
                <Label>Help Text</Label>
                <Input
                  value={editingField.help_text || ''}
                  onChange={(e) => setEditingField({ ...editingField, help_text: e.target.value })}
                  placeholder="Additional instructions"
                  className="mt-1"
                />
              </div>

              <div className="flex items-center justify-between">
                <Label>Required</Label>
                <Switch
                  checked={editingField.required}
                  onCheckedChange={(checked) => setEditingField({ ...editingField, required: checked })}
                />
              </div>

              {/* Options for select/radio/checkbox fields */}
              {['select', 'multiselect', 'radio', 'checkbox'].includes(editingField.field_type) && (
                <div>
                  <Label className="mb-2 block">Options</Label>
                  <div className="space-y-2">
                    {(editingField.options || []).map((opt, idx) => (
                      <div key={opt.option_id} className="flex items-center gap-2">
                        <Input
                          value={opt.label}
                          onChange={(e) => updateOption(opt.option_id, { 
                            label: e.target.value,
                            value: e.target.value.toLowerCase().replace(/\s+/g, '_')
                          })}
                          placeholder={`Option ${idx + 1}`}
                          className="flex-1"
                        />
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => deleteOption(opt.option_id)}
                          disabled={(editingField.options?.length || 0) <= 1}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                    <Button variant="outline" size="sm" onClick={addOption}>
                      <Plus className="w-4 h-4 mr-1" />
                      Add Option
                    </Button>
                  </div>
                </div>
              )}

              {/* Rating scale settings */}
              {editingField.field_type === 'rating' && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Min Value</Label>
                    <Input
                      type="number"
                      value={editingField.min_value || 1}
                      onChange={(e) => setEditingField({ ...editingField, min_value: parseInt(e.target.value) })}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Max Value</Label>
                    <Input
                      type="number"
                      value={editingField.max_value || 5}
                      onChange={(e) => setEditingField({ ...editingField, max_value: parseInt(e.target.value) })}
                      className="mt-1"
                    />
                  </div>
                </div>
              )}

              {/* User Selector settings */}
              {editingField.field_type === 'user_selector' && (
                <div className="space-y-4 p-4 bg-muted/50 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <Users className="w-4 h-4 text-[#4A9CC7]" />
                    <Label className="font-medium">User Selector Options</Label>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Multi-Select</Label>
                      <p className="text-xs text-muted-foreground">Allow selecting multiple users</p>
                    </div>
                    <Switch
                      checked={editingField.user_selector_config?.multi_select || false}
                      onCheckedChange={(checked) => setEditingField({
                        ...editingField,
                        user_selector_config: {
                          ...editingField.user_selector_config,
                          multi_select: checked
                        }
                      })}
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Exclude Self</Label>
                      <p className="text-xs text-muted-foreground">Hide logged-in user from selection</p>
                    </div>
                    <Switch
                      checked={editingField.user_selector_config?.exclude_self !== false}
                      onCheckedChange={(checked) => setEditingField({
                        ...editingField,
                        user_selector_config: {
                          ...editingField.user_selector_config,
                          exclude_self: checked
                        }
                      })}
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Allow Select All</Label>
                      <p className="text-xs text-muted-foreground">Show "Select All" option (multi-select only)</p>
                    </div>
                    <Switch
                      checked={editingField.user_selector_config?.allow_select_all || false}
                      onCheckedChange={(checked) => setEditingField({
                        ...editingField,
                        user_selector_config: {
                          ...editingField.user_selector_config,
                          allow_select_all: checked
                        }
                      })}
                      disabled={!editingField.user_selector_config?.multi_select}
                    />
                  </div>

                  {/* User Groups Filter */}
                  <Separator className="my-4" />
                  <div>
                    <Label className="mb-2 block">Restrict to User Groups</Label>
                    <p className="text-xs text-muted-foreground mb-3">
                      Only users from selected groups will appear in the dropdown. Leave empty to show all users.
                    </p>
                    <div className="border rounded-lg max-h-40 overflow-y-auto p-2 space-y-2">
                      {userGroups.length === 0 ? (
                        <p className="text-sm text-muted-foreground p-2">No user groups available</p>
                      ) : (
                        userGroups.map((group) => (
                          <div key={group.group_id} className="flex items-center space-x-2">
                            <Checkbox
                              id={`group-${group.group_id}`}
                              checked={(editingField.user_selector_config?.allowed_groups || []).includes(group.group_id)}
                              onCheckedChange={(checked) => {
                                const currentGroups = editingField.user_selector_config?.allowed_groups || [];
                                const newGroups = checked
                                  ? [...currentGroups, group.group_id]
                                  : currentGroups.filter(id => id !== group.group_id);
                                setEditingField({
                                  ...editingField,
                                  user_selector_config: {
                                    ...editingField.user_selector_config,
                                    allowed_groups: newGroups
                                  }
                                });
                              }}
                            />
                            <Label htmlFor={`group-${group.group_id}`} className="font-normal text-sm">
                              {group.name} <span className="text-muted-foreground">({group.member_count || 0} members)</span>
                            </Label>
                          </div>
                        ))
                      )}
                    </div>
                    {(editingField.user_selector_config?.allowed_groups || []).length > 0 && (
                      <p className="text-xs text-[#4A9CC7] mt-2">
                        {(editingField.user_selector_config?.allowed_groups || []).length} group(s) selected
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setFieldEditorOpen(false)}>
              Cancel
            </Button>
            <Button onClick={saveFieldEdits} className="bg-[#4A9CC7] hover:bg-[#3d8ab5]">
              Save Field
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Form Settings Dialog */}
      <Dialog open={settingsDialogOpen} onOpenChange={setSettingsDialogOpen}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5 text-[#4A9CC7]" />
              Form Settings
            </DialogTitle>
            <DialogDescription>
              Configure form type, approval workflow, and visibility settings
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Form Type */}
            <div className="space-y-3">
              <Label className="text-sm font-medium">Form Type</Label>
              <Select value={formType} onValueChange={setFormType}>
                <SelectTrigger data-testid="form-type-select">
                  <SelectValue placeholder="Select form type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="standard">Standard Form</SelectItem>
                  <SelectItem value="feedback">Feedback Form</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {formType === 'feedback' 
                  ? 'Feedback forms support user selection and multi-target submissions'
                  : 'Standard forms have single submissions per user'}
              </p>
            </div>

            <Separator />

            {/* Approval Strategy */}
            <div className="space-y-3">
              <Label className="text-sm font-medium">Approval Strategy</Label>
              <Select value={approvalStrategy} onValueChange={setApprovalStrategy}>
                <SelectTrigger data-testid="approval-strategy-select">
                  <SelectValue placeholder="Select approval strategy" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="submitter_approver">Submitter's Approver</SelectItem>
                  <SelectItem value="receiver_manager">Receiver's Manager</SelectItem>
                  <SelectItem value="form_owner">Form Owner</SelectItem>
                  <SelectItem value="static">No Approval Required</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {approvalStrategy === 'submitter_approver' && 'Submissions go to the submitter\'s assigned approver'}
                {approvalStrategy === 'receiver_manager' && 'Submissions go to the selected user\'s manager (for feedback forms)'}
                {approvalStrategy === 'form_owner' && 'All submissions go to the form owner for approval'}
                {approvalStrategy === 'static' && 'Submissions are auto-approved without review'}
              </p>
            </div>

            <Separator />

            {/* Anonymous Settings */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-sm font-medium">Allow Anonymous Submission</Label>
                  <p className="text-xs text-muted-foreground">Hide submitter identity from certain viewers</p>
                </div>
                <Switch
                  checked={allowAnonymous}
                  onCheckedChange={setAllowAnonymous}
                  data-testid="allow-anonymous-switch"
                />
              </div>

              {allowAnonymous && (
                <div className="pl-4 border-l-2 border-muted space-y-3">
                  <Label className="text-sm">Anonymous Visible To</Label>
                  <div className="space-y-2">
                    {[
                      { id: 'receiver', label: 'Receiver', desc: 'Person receiving feedback' },
                      { id: 'manager', label: 'Manager', desc: 'Receiver\'s manager' },
                      { id: 'admin', label: 'Admin', desc: 'Organization administrators' }
                    ].map(role => (
                      <div key={role.id} className="flex items-center space-x-2">
                        <Checkbox
                          id={`anon-${role.id}`}
                          checked={anonymousVisibleTo.includes(role.id)}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setAnonymousVisibleTo([...anonymousVisibleTo, role.id]);
                            } else {
                              setAnonymousVisibleTo(anonymousVisibleTo.filter(r => r !== role.id));
                            }
                          }}
                        />
                        <div>
                          <Label htmlFor={`anon-${role.id}`} className="text-sm font-normal">{role.label}</Label>
                          <p className="text-xs text-muted-foreground">{role.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Info Banner for Feedback Forms */}
            {formType === 'feedback' && (
              <div className="p-3 bg-purple-50 dark:bg-purple-950/30 rounded-lg border border-purple-200 dark:border-purple-800">
                <div className="flex items-start gap-2">
                  <Users className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-purple-800 dark:text-purple-200">Feedback Form Mode</p>
                    <p className="text-xs text-purple-600 dark:text-purple-400 mt-1">
                      Add a "User Selector" field to allow submitters to select feedback recipients. 
                      Each selected user will create a separate submission record.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setSettingsDialogOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Form Dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5 text-[#4A9CC7]" />
              Form Preview
            </DialogTitle>
            <DialogDescription>
              This is how the form will appear to users when they fill it out.
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto py-4">
            <div className="space-y-6 px-1">
              {/* Form Header */}
              <div className="border-b pb-4">
                <h2 className="text-xl font-semibold">{formName || 'Untitled Form'}</h2>
                {formDescription && (
                  <p className="text-muted-foreground mt-1">{formDescription}</p>
                )}
              </div>

              {/* Form Fields */}
              {fields.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <p>No fields added yet</p>
                </div>
              ) : (
                fields.map(field => renderPreviewField(field))
              )}
            </div>
          </div>

          <DialogFooter className="border-t pt-4">
            <div className="flex items-center gap-2 w-full justify-between">
              <p className="text-xs text-muted-foreground">
                {fields.length} field(s) • Preview only - no data will be saved
              </p>
              <Button onClick={() => setPreviewOpen(false)}>
                Close Preview
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
    </AppLayout>
  );
};

export default FormBuilder;
